import {
  applyDesignerCommand,
  arrangeCounts,
  arrangementsFor,
  DESIGNER_ARRANGEMENTS,
  type DesignAreaAnswer,
  type DesignerArrangement,
} from '@pz/scene';
import { el, type DesignerCtx } from './panels.js';
import { run } from './commands.js';
import { paintThumb } from './thumbs.js';

/**
 * "Rasporedi": a row of small pictures, each one the shopper's OWN design
 * already rearranged — photo on top, name beside it, words over the picture.
 * Tapping one applies it (one undo step). It is how a design stops looking
 * dropped-in without anyone touching a handle.
 *
 * The row is recomputed a moment after the design settles, never during a
 * drag; each picture comes from the real renderer (`paintThumb`), so what is
 * shown is exactly what tapping produces. The arrangement the design already
 * has is marked, so the row also answers "which one is this?".
 */

export interface ArrangeStripHandle {
  readonly element: HTMLElement;
  /** Hide or show from the shell (a phone hides it while a panel is open). */
  setSuppressed(on: boolean): void;
  destroy(): void;
}

/**
 * Whether two designs are laid out the same, give or take the canvas's own
 * re-fit of a text box to its ink after the arrangement landed (a fraction of
 * a millimetre in position, a few percent in size).
 */
export function sameLayout(a: DesignAreaAnswer, b: DesignAreaAnswer): boolean {
  if (a.layers.length !== b.layers.length) return false;
  return a.layers.every((layer, index) => {
    const other = b.layers[index]!;
    if (other.id !== layer.id) return false;
    const size = Math.max(layer.box.w, layer.box.h, 1);
    return Math.abs(layer.box.x - other.box.x) <= Math.max(1.2, size * 0.06)
      && Math.abs(layer.box.y - other.box.y) <= Math.max(1.2, size * 0.06)
      && Math.abs(layer.box.w - other.box.w) <= Math.max(1.2, layer.box.w * 0.1)
      && Math.abs(layer.box.h - other.box.h) <= Math.max(1.2, layer.box.h * 0.1);
  });
}

export function createArrangeStrip(ctx: DesignerCtx, options: { compact?: boolean; onToggle?: (visible: boolean) => void; onLayout?: (height: number) => void } = {}): ArrangeStripHandle {
  const wrap = el('div', `pzd-arrange${options.compact ? ' is-compact' : ''}`);
  wrap.setAttribute('role', 'group');
  wrap.setAttribute('aria-label', ctx.texts.arrangeTitle);
  const head = el('div', 'pzd-arrange-head');
  head.append(el('strong', undefined, ctx.texts.arrangeTitle));
  if (!options.compact) head.append(el('span', 'pzd-hint', ctx.texts.arrangeHint));
  const row = el('div', 'pzd-arrange-row');
  wrap.append(head, row);
  wrap.hidden = true;

  let shown = false;
  /** Hides or shows the row, telling the shell so it can make room. */
  const show = (visible: boolean): void => {
    wrap.hidden = !visible;
    if (visible !== shown) { shown = visible; options.onToggle?.(visible); }
  };

  let suppressed = false;
  let destroyed = false;
  let generation = 0;
  let timer: number | undefined;
  let painted = '';

  function paint(): void {
    if (destroyed) return;
    const answer = ctx.store.value();
    const counts = arrangeCounts(answer, ctx.rawConfig);
    // One lonely element has nothing to arrange against.
    const worth = counts.photos + counts.words + counts.decor >= 2;
    const variants = worth ? arrangementsFor(counts) : [];
    const signature = JSON.stringify([answer, variants]);
    show(!(suppressed || variants.length === 0 || ctx.view.review));
    if (wrap.hidden || signature === painted) return;
    painted = signature;
    const current = ++generation;
    row.textContent = '';
    for (const variant of variants) {
      let result: DesignAreaAnswer;
      try {
        result = applyDesignerCommand(ctx.rawConfig, answer, { op: 'arrange', variant });
      } catch {
        continue;
      }
      const name = DESIGNER_ARRANGEMENTS.find((item) => item.id === variant)?.name ?? variant;
      const button = el('button', 'pzd-arrange-card');
      button.type = 'button';
      button.title = name;
      button.setAttribute('aria-label', `${ctx.texts.arrangeTitle}: ${name}`);
      const on = sameLayout(result, answer);
      button.classList.toggle('is-on', on);
      button.setAttribute('aria-pressed', String(on));
      const thumb = el('canvas');
      thumb.setAttribute('aria-hidden', 'true');
      button.append(thumb);
      if (!options.compact) button.append(el('span', undefined, name));
      button.addEventListener('click', () => apply(variant));
      row.append(button);
      void paintThumb(ctx, thumb, result, () => destroyed || current !== generation, options.compact ? 120 : 150);
    }
    if (!row.childElementCount) show(false);
    // Measured synchronously: a ResizeObserver is not delivered while the
    // document is hidden, and the shell must still make room for the row.
    else options.onLayout?.(wrap.offsetHeight);
  }

  function apply(variant: DesignerArrangement): void {
    const selected = ctx.selected()?.id ?? null;
    if (run(ctx, { op: 'arrange', variant })) {
      // Keep whatever was selected; the arrangement moved it, not replaced it.
      if (selected) ctx.dispatch({ type: 'select', id: selected });
    }
  }

  function schedule(): void {
    window.clearTimeout(timer);
    // After the shopper stops: a drag or a typed word must not rebuild six
    // renders per frame.
    timer = window.setTimeout(paint, 450);
  }

  const stop = ctx.store.subscribe((state, previous) => {
    if (state.answer !== previous.answer) schedule();
  });
  schedule();

  return {
    element: wrap,
    setSuppressed(on) {
      if (suppressed === on) return;
      suppressed = on;
      if (on) show(false);
      else { painted = ''; schedule(); }
    },
    destroy() {
      destroyed = true;
      window.clearTimeout(timer);
      stop();
      wrap.remove();
    },
  };
}
